import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  InventoryPerProduct,
  fetchZonesByLocationId,
  InventoryTransfers,
  InventoryBatches,
  getProductInventory,
  fetchAvailabilityRebuildPlan,
  applyAvailabilityRebuild,
  getRcks,
  getShelves,
  getSpots,
} from "../../../server/InventoryUpdate";
import { useSelector, useDispatch } from "react-redux";
import ProcductLocationTable from "./ProcductLocationTable.js";
import TransferDestinationEditor from "./TransferDestinationEditor.js";
import ProductDetailsPopUp from "../inventoryUpdate/ProductDetailsPopUp.js";
import { useLocation } from "react-router-dom";
import * as actionType from "../../../store/action";
import showAlert, {
  showAlertWithConfirm,
  showAlertWithConfirmCancel,
} from "../../../server/ShowAlert";
import {
  adjustmentFromSpotId,
  asZoneList,
  attachZoneMeta,
  canTransferInventorySpot,
  filterDestinationZones,
  findAdjustmentSourceZone,
  findBatchForSpot,
  findSortingZone,
  getSpotId,
  isSameInventorySpot,
  isSortingZone,
  movementValidationError,
  postInventoryMovement,
  zoneRecordId,
  ZONE_TYPE,
} from "../../../utils/inventoryTransfer";
import { getProductHeaderMetaParts } from "../../../utils/formatProductPackSize";
import {
  catalogQuantityOf,
  catalogUpdatesFromRebuild,
  readApiErrorText,
  rebuildPlanConfirmText,
  rebuildPlanCounts,
  rebuildRequestErrorMessage,
  rebuildResultAlert,
} from "../../../utils/inventoryAvailability";

const INVENTORY_SUMMARY_FIELDS = [
  { key: "totalInventory", label: "מלאי כללי" },
  { key: "availableInventory", label: "מלאי זמין" },
  { key: "sellableInventory", label: "מלאי למכירה" },
  { key: "reservedInventory", label: "מלאי מוקפא" },
  { key: "damagedInventory", label: "מלאי פגום" },
  { key: "soldInventory", label: "מלאי מכור" },
  { key: "expiredInventory", label: "מלאי פג תוקף" },
];

const EMPTY_PRODUCT_INVENTORY = {
  totalInventory: 0,
  availableInventory: 0,
  sellableInventory: 0,
  reservedInventory: 0,
  damagedInventory: 0,
  soldInventory: 0,
  expiredInventory: 0,
  availabilityRecord: null,
};

const parseInventoryBatches = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.items)) return raw.items;
  return [];
};

const normalizeProductInventory = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const availabilityRecord =
    raw.availabilityRecord == null ||
    Number.isNaN(Number(raw.availabilityRecord))
      ? null
      : Number(raw.availabilityRecord);
  return {
    totalInventory: raw.totalInventory ?? 0,
    availableInventory: raw.availableInventory ?? 0,
    sellableInventory: raw.sellableInventory ?? 0,
    reservedInventory: raw.reservedInventory ?? 0,
    damagedInventory: raw.damagedInventory ?? 0,
    soldInventory: raw.soldInventory ?? 0,
    expiredInventory: raw.expiredInventory ?? 0,
    availabilityRecord,
  };
};

const hasAvailabilityRecordDrift = (inventory) => {
  if (!inventory) return false;
  if (inventory.availabilityRecord == null) return true;
  return (
    Number(inventory.availabilityRecord) !==
    Number(inventory.availableInventory)
  );
};

async function resolveAdjustmentSpotId(zoneId) {
  const fallback = `${zoneId}-01-01-01`;
  try {
    const racks = asZoneList((await getRcks(zoneId))?.data);
    const rack =
      racks.find(
        (item) => parseInt(String(item?.rackId).split("-").pop(), 10) === 1,
      ) || racks[0];
    if (!rack?.rackId) return fallback;
    const rackKey = String(rack.rackId).startsWith(`${zoneId}-`)
      ? String(rack.rackId)
      : `${zoneId}-${rack.rackId}`;
    const shelves = asZoneList((await getShelves(rackKey))?.data);
    const shelf =
      shelves.find(
        (item) =>
          parseInt(String(item?.rackShelfId).split("-").pop(), 10) === 1,
      ) || shelves[0];
    if (!shelf?.rackShelfId) return fallback;
    const shelfKey = String(shelf.rackShelfId).startsWith(`${zoneId}-`)
      ? String(shelf.rackShelfId)
      : `${zoneId}-${shelf.rackShelfId}`;
    const spots = asZoneList((await getSpots(shelfKey))?.data);
    return adjustmentFromSpotId(zoneId, spots);
  } catch (err) {
    console.error("Error resolving adjustment spot:", err);
    return fallback;
  }
}

const confirmBranchRebuild = (summary) =>
  new Promise((resolve) => {
    showAlertWithConfirmCancel({
      msgTitle: "לעדכן זמינות לכל המוצרים בסניף?",
      msgText: rebuildPlanConfirmText(summary),
      confirmText: "עדכון זמינות",
      cancelText: "ביטול",
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

const ProductLocationPopUp = ({
  product,
  isModal = false,
  onInventoryUpdated,
  onCatalogAvailabilityUpdated,
}) => {
  const stationId = useSelector((state) => state.user.stationId);
  const selectedProduct = useSelector((state) => state.selectedProduct.product);
  const rawItems = useSelector((state) => state.displayProduct.itemsShow);

  const dispatch = useDispatch();

  const items = Array.isArray(rawItems) ? rawItems : [];

  const [productOverride, setProductOverride] = useState(null);
  const [location, setLocation] = useState([]);
  const [locations, setLocations] = useState(null);
  const [hasReturn, setHasReturn] = useState(false);
  const [selectedSrc, setSelectedSrc] = useState(null);
  const [detailsPopUp, setDetailsPopUp] = useState(false);
  const [inventoryBatches, setInventoryBatches] = useState([]);
  const [productInventory, setProductInventory] = useState(null);
  const [loadingProductInventory, setLoadingProductInventory] = useState(true);
  const [productInventoryError, setProductInventoryError] = useState(false);
  const [spotsLoadError, setSpotsLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rebuildingAvailability, setRebuildingAvailability] = useState(false);
  const [rebuildPhase, setRebuildPhase] = useState(null);

  const pathname = useLocation().pathname;
  const mountedRef = useRef(true);
  const inventoryRequestRef = useRef(0);

  let resolvedProduct = productOverride;
  if (!resolvedProduct) {
    if (
      product &&
      product.productId != null &&
      String(selectedProduct?.productId) === String(product.productId)
    ) {
      resolvedProduct = selectedProduct;
    } else {
      resolvedProduct = product;
    }
  }
  if (!resolvedProduct && selectedProduct) {
    const matchingProduct = items.find(
      (item) => item.gtin === selectedProduct.gtin,
    );
    resolvedProduct = matchingProduct || selectedProduct;
  }

  const productBarcod = resolvedProduct?.productId ?? null;

  useEffect(() => {
    setProductOverride(null);
  }, [product?.productId, selectedProduct?.productId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadZones = async () => {
      try {
        const res = await fetchZonesByLocationId(stationId);
        if (cancelled) return;
        setLocations(asZoneList(res?.data));
      } catch (err) {
        console.error("Error fetching zones:", err);
      }
    };

    loadZones();
    return () => {
      cancelled = true;
    };
  }, [stationId]);

  const enrichSpots = useCallback(
    (spotsList) =>
      (spotsList || []).map((spot) => {
        const enriched = attachZoneMeta(spot, locations || [], stationId);
        return {
          ...enriched,
          zoneName:
            enriched.zoneName ?? spot.zone?.name ?? spot.locationName ?? null,
        };
      }),
    [locations, stationId],
  );

  const refreshInventorySpots = useCallback(async () => {
    if (!productBarcod || !stationId) return;
    try {
      const res = await InventoryPerProduct(productBarcod, stationId);
      if (!mountedRef.current) return;
      const items = enrichSpots(res?.data?.items || []);
      setLocation(items);
      setHasReturn(true);
      setSpotsLoadError(false);
      return items;
    } catch (err) {
      console.error("Error loading InventoryPerProduct:", err);
      if (mountedRef.current) {
        setLocation([]);
        setSpotsLoadError(true);
        setHasReturn(true);
      }
    }
  }, [productBarcod, stationId, enrichSpots]);

  const refreshProductInventory = useCallback(async () => {
    const requestId = ++inventoryRequestRef.current;
    const stillCurrent = () =>
      mountedRef.current && requestId === inventoryRequestRef.current;

    if (!productBarcod || !stationId) {
      if (stillCurrent()) {
        setProductInventory(null);
        setProductInventoryError(false);
        setLoadingProductInventory(false);
      }
      return null;
    }
    if (mountedRef.current) setLoadingProductInventory(true);
    try {
      const raw = await getProductInventory(productBarcod, stationId);
      if (!stillCurrent()) return null;
      const inventory = normalizeProductInventory(raw);
      setProductInventory(inventory ?? { ...EMPTY_PRODUCT_INVENTORY });
      setProductInventoryError(false);
      return inventory;
    } catch (err) {
      console.error("Error loading product inventory:", err);
      if (stillCurrent()) {
        setProductInventory(null);
        setProductInventoryError(true);
      }
      return null;
    } finally {
      if (stillCurrent()) setLoadingProductInventory(false);
    }
  }, [productBarcod, stationId]);

  useEffect(() => {
    let cancelled = false;

    const loadInventory = async () => {
      try {
        const res = await InventoryPerProduct(productBarcod, stationId);
        if (cancelled) return;
        setLocation(enrichSpots(res?.data?.items || []));
        setHasReturn(true);
        setSpotsLoadError(false);
      } catch (err) {
        console.error("Error loading InventoryPerProduct:", err);
        if (!cancelled) {
          setLocation([]);
          setSpotsLoadError(true);
          setHasReturn(true);
        }
      }
    };

    loadInventory();
    return () => {
      cancelled = true;
    };
  }, [productBarcod, stationId, enrichSpots]);

  useEffect(() => {
    let cancelled = false;

    const loadInventoryBatch = async () => {
      if (!productBarcod || !stationId) {
        return;
      }
      try {
        const res = await InventoryBatches(productBarcod, stationId);
        if (cancelled) return;

        const raw = res?.data;
        const batches = parseInventoryBatches(raw);
        if (!Array.isArray(raw) && raw != null && !Array.isArray(raw?.items)) {
          console.warn("InventoryBatches: unexpected response shape", raw);
        }
        setInventoryBatches(batches);
      } catch (err) {
        console.error("Error loading InventoryBatches:", err);
        setInventoryBatches([]);
      }
    };

    loadInventoryBatch();
    return () => {
      cancelled = true;
    };
  }, [productBarcod, stationId, location]);

  useEffect(() => {
    refreshProductInventory();
  }, [refreshProductInventory]);

  const sortingZone = useMemo(() => findSortingZone(locations), [locations]);
  const adjustmentZone = useMemo(
    () => findAdjustmentSourceZone(locations),
    [locations],
  );

  const resolveSortingSpot = useCallback(() => {
    const sortingZoneId = zoneRecordId(sortingZone);
    return (
      location.find(
        (spot) =>
          (sortingZoneId && String(spot.zoneId) === sortingZoneId) ||
          isSortingZone(spot),
      ) || null
    );
  }, [sortingZone, location]);

  const canShowTransferForSpot = useCallback(
    (spot) =>
      canTransferInventorySpot(spot, {
        batch: findBatchForSpot(inventoryBatches, spot),
      }),
    [inventoryBatches],
  );

  const resolveHeldInventorySpot = useCallback(() => {
    const sortingSpot = resolveSortingSpot();
    if (
      sortingSpot &&
      getSpotId(sortingSpot) &&
      canShowTransferForSpot(sortingSpot)
    ) {
      return sortingSpot;
    }
    return (
      (location || []).find(
        (spot) => getSpotId(spot) && canShowTransferForSpot(spot),
      ) ||
      (location || []).find((spot) => getSpotId(spot)) ||
      null
    );
  }, [resolveSortingSpot, location, canShowTransferForSpot]);

  const sourceSpotForRules = useMemo(() => {
    if (!selectedSrc) return null;
    if (selectedSrc !== -1) return selectedSrc;
    return (
      resolveHeldInventorySpot() || {
        zoneType: ZONE_TYPE.ENTRY,
        locationId: stationId,
      }
    );
  }, [selectedSrc, resolveHeldInventorySpot, stationId]);

  const destinationZones = useMemo(() => {
    if (!sourceSpotForRules) return [];
    return filterDestinationZones(locations || [], sourceSpotForRules, {
      batch: findBatchForSpot(inventoryBatches, sourceSpotForRules),
      locationId: stationId,
    });
  }, [locations, sourceSpotForRules, inventoryBatches, stationId]);

  const handleRowClick = useCallback(
    (row) => {
      if (!canShowTransferForSpot(row)) return;
      setSelectedSrc(row);
    },
    [canShowTransferForSpot],
  );

  const inventorySummary = productInventory;

  const productAvailability = inventorySummary?.availableInventory;
  const showAvailabilityDrift = hasAvailabilityRecordDrift(inventorySummary);

  const notifyInventoryUpdated = useCallback(
    (inventory, availableQuantity = inventory?.availableInventory ?? 0) => {
      const productId = resolvedProduct?.productId ?? productBarcod;
      if (!productId || !onInventoryUpdated || !inventory) return;
      onInventoryUpdated(productId, {
        availableInventory: inventory.availableInventory ?? 0,
        availableQuantity,
      });
    },
    [resolvedProduct?.productId, productBarcod, onInventoryUpdated],
  );

  const handleExpirationUpdated = useCallback(async () => {
    const inventory = await refreshProductInventory();
    if (inventory) {
      notifyInventoryUpdated(inventory, catalogQuantityOf(inventory));
    }
  }, [refreshProductInventory, notifyInventoryUpdated]);

  const handleRebuildAvailability = useCallback(async () => {
    if (!stationId || rebuildingAvailability) return;
    setRebuildingAvailability(true);
    setRebuildPhase("plan");
    let applying = false;
    try {
      const plan = await fetchAvailabilityRebuildPlan(stationId);
      if (!mountedRef.current) return;
      if (!plan?.summary) {
        showAlert({
          msgTitle: "לא ניתן לקרוא את תוכנית העדכון",
          msgType: "error",
        });
        return;
      }

      const { toWrite, inSync, skipped } = rebuildPlanCounts(plan.summary);
      if (toWrite <= 0) {
        const inventory = await refreshProductInventory();
        if (inventory) {
          notifyInventoryUpdated(inventory, catalogQuantityOf(inventory));
        }
        showAlert({
          msgTitle: "אין רשומות לעדכון",
          msgText: `תואמים: ${inSync}. דילוג: ${skipped}.`,
          msgType: "info",
        });
        return;
      }

      const confirmed = await confirmBranchRebuild(plan.summary);
      if (!confirmed || !mountedRef.current) return;

      applying = true;
      setRebuildPhase("apply");
      const result = await applyAvailabilityRebuild(stationId);
      if (!mountedRef.current) return;

      const updates = catalogUpdatesFromRebuild(result);
      if (updates.length && onCatalogAvailabilityUpdated) {
        onCatalogAvailabilityUpdated(updates);
      }
      const inventory = await refreshProductInventory();
      if (inventory) {
        const productId = String(resolvedProduct?.productId ?? productBarcod);
        const fromReport = updates.find(
          (row) => String(row.productId) === productId,
        );
        notifyInventoryUpdated(
          inventory,
          fromReport
            ? fromReport.availableQuantity
            : catalogQuantityOf(inventory),
        );
      }

      if (!result?.summary) {
        showAlertWithConfirm({
          msgTitle: "לא התקבל דוח עדכון",
          msgText: "בדקו את הזמינות לפני ניסיון נוסף.",
          msgType: "warning",
          confirmText: "הבנתי",
        });
        return;
      }

      const alert = rebuildResultAlert(result);
      if (alert.partial) {
        showAlertWithConfirm({
          msgTitle: alert.msgTitle,
          msgText: alert.msgText,
          msgType: alert.msgType,
          confirmText: "הבנתי",
        });
      } else {
        showAlert({
          msgTitle: alert.msgTitle,
          msgText: alert.msgText,
          msgType: alert.msgType,
        });
      }
    } catch (err) {
      console.error("Error rebuilding availability:", err);
      if (!mountedRef.current) return;
      showAlert({
        msgTitle: applying
          ? rebuildRequestErrorMessage(err)
          : readApiErrorText(err) || "שגיאה בטעינת תוכנית העדכון",
        msgType: "error",
      });
    } finally {
      if (mountedRef.current) {
        setRebuildingAvailability(false);
        setRebuildPhase(null);
      }
    }
  }, [
    stationId,
    rebuildingAvailability,
    refreshProductInventory,
    notifyInventoryUpdated,
    onCatalogAvailabilityUpdated,
    resolvedProduct?.productId,
    productBarcod,
  ]);

  const resolveBatchId = useCallback(() => {
    if (selectedSrc && selectedSrc !== -1) {
      return selectedSrc.batchId || null;
    }
    if (selectedSrc === -1) {
      return (
        sourceSpotForRules?.batchId ||
        resolveSortingSpot()?.batchId ||
        location[0]?.batchId ||
        null
      );
    }
    return null;
  }, [selectedSrc, sourceSpotForRules, resolveSortingSpot, location]);

  const refreshAfterMovement = useCallback(async () => {
    const spots = await refreshInventorySpots();
    let batches = inventoryBatches;
    try {
      const batchesRes = await InventoryBatches(productBarcod, stationId);
      batches = parseInventoryBatches(batchesRes?.data);
      if (mountedRef.current) {
        setInventoryBatches(batches);
      }
    } catch (batchErr) {
      console.error("Error refreshing InventoryBatches:", batchErr);
    }
    const inventory = await refreshProductInventory();
    return { batches, spots: spots || [], inventory };
  }, [
    refreshInventorySpots,
    inventoryBatches,
    productBarcod,
    stationId,
    refreshProductInventory,
  ]);

  const handleTransferSubmit = useCallback(
    async ({ to, quantity, destinationZoneType }) => {
      if (submitting) return;

      const sortingZoneId =
        selectedSrc === -1 ? zoneRecordId(adjustmentZone) : "";
      if (selectedSrc === -1 && !sortingZoneId) {
        showAlert({
          msgTitle: "לא נמצא אזור מיון להעברה",
          msgType: "error",
        });
        return;
      }

      const from =
        selectedSrc === -1
          ? getSpotId(sourceSpotForRules) ||
            (await resolveAdjustmentSpotId(sortingZoneId))
          : selectedSrc?.spotId;

      if (!from || !to) {
        showAlert({
          msgTitle: "יש לבחור מיקום יעד מלא",
          msgType: "error",
        });
        return;
      }

      const batchId = resolveBatchId();
      if (!batchId) {
        showAlert({
          msgTitle: "לא נמצא מזהה אצווה להעברה",
          msgType: "error",
        });
        return;
      }

      const sourceBatch = findBatchForSpot(
        inventoryBatches,
        sourceSpotForRules,
      );
      const destZone =
        (locations || []).find(
          (zone) =>
            String(to) === String(zone.id) ||
            String(to).startsWith(`${zone.id}-`),
        ) ||
        (locations || []).find((zone) => zone.type === destinationZoneType);

      const hasRealSource = Boolean(getSpotId(sourceSpotForRules));
      const validationError = hasRealSource
        ? movementValidationError({
            from: sourceSpotForRules,
            toSpotId: to,
            toLocationId: destZone?.locationId || stationId,
            toZoneType: destZone?.type ?? destinationZoneType,
            quantity: Number(quantity),
            productAvailability,
            batch: sourceBatch,
          })
        : isSameInventorySpot(from, to)
          ? "לא ניתן להעביר מלאי לאותו תא"
          : !(Number(quantity) > 0)
            ? "יש להזין כמות תקינה"
            : null;
      if (validationError) {
        showAlert({ msgTitle: validationError, msgType: "error" });
        return;
      }

      setSubmitting(true);
      try {
        const result = await postInventoryMovement({
          send: InventoryTransfers,
          batchId,
          quantity: Number(quantity),
          from,
          to,
          refresh: refreshAfterMovement,
        });

        if (!result.ok) {
          showAlert({ msgTitle: result.message, msgType: "error" });
          return;
        }

        const { inventory } = await refreshAfterMovement();
        notifyInventoryUpdated(inventory);
        showAlert({
          msgTitle: "תא עודכן בהצלחה",
          msgType: "success",
        });
        setSelectedSrc(null);
      } catch (err) {
        console.error("Error in handleTransferSubmit:", err);
        showAlert({
          msgTitle: "שגיאה בהעברת המלאי",
          msgType: "error",
        });
      } finally {
        if (mountedRef.current) setSubmitting(false);
      }
    },
    [
      submitting,
      selectedSrc,
      adjustmentZone,
      resolveBatchId,
      inventoryBatches,
      sourceSpotForRules,
      locations,
      stationId,
      productAvailability,
      refreshAfterMovement,
      notifyInventoryUpdated,
    ],
  );

  const handleInvAdj = useCallback(() => {
    const heldSpot = resolveHeldInventorySpot();
    if (heldSpot && canShowTransferForSpot(heldSpot)) {
      setSelectedSrc(heldSpot);
      return;
    }
    setSelectedSrc(-1);
  }, [resolveHeldInventorySpot, canShowTransferForSpot]);

  if (!resolvedProduct) {
    return <div>לא נמצא פריט להצגה</div>;
  }

  const productDisplayName =
    resolvedProduct.name ||
    resolvedProduct.productName ||
    resolvedProduct.ProductName ||
    "";
  const productHeaderMetaParts = getProductHeaderMetaParts(resolvedProduct);

  const isProductSettings = pathname.startsWith("/edit-product/");
  const showTransfer = isProductSettings || isModal;
  const showProductTitle =
    Boolean(productDisplayName) && !isProductSettings && !isModal;

  const body = (
    <>
      <div className="product-location">
        <div className="product-header">
          {loadingProductInventory ? (
            <span className="product-header__status">טוען נתוני מלאי...</span>
          ) : productInventoryError ? (
            <span className="product-header__status">
              שגיאה בטעינת נתוני מלאי
            </span>
          ) : inventorySummary ? (
            <>
              {INVENTORY_SUMMARY_FIELDS.map(({ key, label }) => (
                <span key={key} className="product-header__chip">
                  <span className="product-header__chip-label">{label}</span>
                  <span className="product-header__chip-value">
                    {inventorySummary[key]}
                  </span>
                </span>
              ))}
              {showAvailabilityDrift ? (
                <span
                  className="product-header__chip product-header__chip--drift"
                  title="הכמות השמורה למוצר במיקום לצורכי קטלוג והזמנות. כשאינה תואמת את המלאי הזמין בפועל — יש סטייה."
                >
                  <span className="product-header__chip-label">
                    זמינות בקטלוג
                  </span>
                  <span className="product-header__chip-value">
                    {inventorySummary.availabilityRecord == null
                      ? "חסרה"
                      : inventorySummary.availabilityRecord}
                  </span>
                  <span className="product-header__chip-hint">
                    (זמין בפועל: {inventorySummary.availableInventory})
                  </span>
                  <button
                    type="button"
                    className="product-header__rebuild-btn"
                    disabled={rebuildingAvailability}
                    onClick={() => void handleRebuildAvailability()}
                    title="מציג תוכנית עדכון לכל המוצרים בסניף, ומעדכן רק אחרי אישור"
                  >
                    {rebuildPhase === "plan"
                      ? "בודק…"
                      : rebuildingAvailability
                        ? "מעדכן…"
                        : "ריענון זמינות"}
                  </button>
                </span>
              ) : null}
            </>
          ) : (
            <span className="product-header__status">לא נמצאו נתוני מלאי</span>
          )}
        </div>
        <button
          type="button"
          className="ui-btn-brand product-location__details-btn"
          onClick={() => {
            dispatch({
              type: actionType.SET_SELCTED_PRODUCT,
              data: { Product: resolvedProduct },
            });
            setDetailsPopUp(true);
          }}
        >
          פרטי מוצר
        </button>
      </div>

      <div className="tables">
        <div className="half-side">
          <div className="half-side__panel">
            <div className="half-side__title">מיקומי מלאי</div>
            <ProcductLocationTable
              locations={location}
              selectedRow={selectedSrc}
              hasReturn={hasReturn}
              loadError={spotsLoadError}
              handleRowClick={handleRowClick}
              isRowSelectable={canShowTransferForSpot}
              onExpirationUpdated={handleExpirationUpdated}
            />
          </div>
          {showTransfer && (
            <button
              type="button"
              className="ui-btn-brand inv-adj-btn"
              onClick={handleInvAdj}
            >
              העברה מהתאמות מלאי
            </button>
          )}
        </div>

        {showTransfer && (
          <div className="half-side">
            <div className="half-side__panel">
              <div className="half-side__title">העברה</div>
              {selectedSrc ? (
                <TransferDestinationEditor
                  zones={destinationZones}
                  productId={productBarcod}
                  selectedSrc={selectedSrc}
                  productAvailability={productAvailability}
                  onSubmit={handleTransferSubmit}
                  submitting={submitting}
                />
              ) : (
                <p className="half-side__placeholder">בחר מיקום מלאי להעברה</p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div
      className={`product-location-popup${
        showTransfer ? " product-location-popup--in-settings" : ""
      }${isModal ? " product-location-popup--modal" : ""}`}
    >
      {isModal ? (
        <>
          <header className="product-location-popup__modal-header">
            <h2 className="product-location-popup__modal-title">העברת מלאי</h2>
            {productHeaderMetaParts.length ? (
              <p className="product-location-popup__modal-name">
                {productHeaderMetaParts.join(" | ")}
              </p>
            ) : null}
            {resolvedProduct.gtin ? (
              <p className="product-location-popup__modal-gtin">
                ברקוד: {resolvedProduct.gtin}
              </p>
            ) : null}
          </header>
          <div className="product-location-popup__modal-scroll">{body}</div>
        </>
      ) : (
        <>
          {showProductTitle ? (
            <h3 className="product-location-popup__title">
              {productDisplayName}
            </h3>
          ) : null}
          {body}
        </>
      )}

      {detailsPopUp && (
        <ProductDetailsPopUp
          product={resolvedProduct}
          onProductUpdated={setProductOverride}
          CloseProductDetails={() => setDetailsPopUp(false)}
        />
      )}
    </div>
  );
};

export default ProductLocationPopUp;
